// NormShift — canonical data for the crosswalk
// All numbers/mappings match the real product story:
//   evidence "Access Review 2026-Q3" → 22 controls across 15 frameworks
// Framework list, control codes, topics, and evidence chips.

export const FRAMEWORKS = [
  { id: "iso27001",   name: "ISO 27001",       family: "Security", code: "A.5.23",       tier: "Free" },
  { id: "soc2",       name: "SOC 2",           family: "Security", code: "CC6.1" },
  { id: "gdpr",       name: "GDPR",            family: "Privacy",  code: "Art.32" },
  { id: "dpdp",       name: "DPDP",            family: "Privacy",  code: "§8" },
  { id: "hipaa",      name: "HIPAA",           family: "Sector",   code: "§164.312(a)" },
  { id: "pci",        name: "PCI DSS",         family: "Sector",   code: "8.2.1" },
  { id: "nistcsf",    name: "NIST CSF",        family: "Security", code: "PR.AC-4" },
  { id: "nist80053",  name: "NIST 800-53",     family: "Security", code: "AC-2" },
  { id: "iso42001",   name: "ISO 42001",       family: "AI",       code: "6.2.1" },
  { id: "iso27701",   name: "ISO 27701",       family: "Privacy",  code: "A.7.4.6" },
  { id: "iso27017",   name: "ISO 27017",       family: "Security", code: "CLD.6.3" },
  { id: "iso27018",   name: "ISO 27018",       family: "Privacy",  code: "A.11.4" },
  { id: "cisv8",      name: "CIS v8",          family: "Security", code: "6.1" },
  { id: "ccpa",       name: "CCPA / CPRA",     family: "Privacy",  code: "§1798.100" },
  { id: "sox",        name: "SOX",             family: "Sector",   code: "§404" },
  { id: "soc1",       name: "SOC 1",           family: "Sector",   code: "CC5.2" },
  { id: "fedramp",    name: "FedRAMP",         family: "Security", code: "AC-2" },
  { id: "tisax",      name: "TISAX",           family: "Sector",   code: "1.2.1" },
  { id: "ce",         name: "Cyber Essentials",family: "Security", code: "CE.1" },
  { id: "e8",         name: "Essential Eight", family: "Security", code: "E8.1" },
];

// A subset of the ~68 canonical topics — the ones that light up
// when the visitor drops "Access Review 2026-Q3" as evidence.
export const HERO_TOPICS = [
  { id: "access-review",  label: "Access Review" },
  { id: "least-priv",     label: "Least Privilege" },
  { id: "authorization",  label: "Authorization" },
  { id: "priv-access",    label: "Privileged Access" },
  { id: "session-mgmt",   label: "Session Mgmt" },
  { id: "authn",          label: "Authentication" },
  { id: "audit-logging",  label: "Audit Logging" },
  { id: "onboarding",     label: "Access Provisioning" },
  { id: "offboarding",    label: "Access Revocation" },
];

// evidence → topics → frameworks (which controls it satisfies)
// The Access Review evidence yields 22 controls across 15 frameworks
// (matches the headline proof number).
export const EVIDENCE = [
  {
    id: "access-review",
    file: "access-review-2026-Q3.pdf",
    label: "Access Review",
    topics: ["access-review", "least-priv", "authorization", "priv-access", "onboarding", "offboarding", "audit-logging"],
    frameworks: ["iso27001","soc2","gdpr","dpdp","hipaa","pci","nistcsf","nist80053","iso27701","iso27017","cisv8","ccpa","fedramp","tisax","e8"],
    counts: { controls: 22, frameworks: 15 },
  },
  {
    id: "encryption-policy",
    file: "encryption-policy-v3.2.pdf",
    label: "Encryption Policy",
    topics: ["access-review", "authn", "authorization", "session-mgmt", "audit-logging"],
    frameworks: ["iso27001","soc2","gdpr","dpdp","hipaa","pci","nistcsf","nist80053","iso27017","iso27018","cisv8","fedramp"],
    counts: { controls: 18, frameworks: 12 },
  },
  {
    id: "pentest",
    file: "pentest-report-2026-Q2.pdf",
    label: "Pen Test Report",
    topics: ["authn","session-mgmt","priv-access","audit-logging"],
    frameworks: ["iso27001","soc2","pci","nistcsf","nist80053","cisv8","fedramp","tisax","ce","e8","hipaa"],
    counts: { controls: 14, frameworks: 11 },
  },
  {
    id: "vendor-dpa",
    file: "vendor-dpa-signed.pdf",
    label: "Vendor DPA",
    topics: ["authorization","onboarding","offboarding","audit-logging","authn"],
    frameworks: ["iso27001","soc2","gdpr","dpdp","hipaa","iso27701","iso27017","iso27018","ccpa","fedramp","tisax","nistcsf","nist80053"],
    counts: { controls: 16, frameworks: 13 },
  },
  {
    id: "backup-test",
    file: "backup-restore-test-Q3.log",
    label: "Backup Test",
    topics: ["audit-logging","authorization","priv-access"],
    frameworks: ["iso27001","soc2","hipaa","pci","nistcsf","nist80053","cisv8","fedramp","tisax"],
    counts: { controls: 12, frameworks: 9 },
  },
];

// The 9 modules
export const MODULES = [
  { code: "01", name: "Controls & Crosswalk", copy: "Every framework, mapped to one canonical layer of ~68 topics." },
  { code: "02", name: "Evidence",             copy: "Attach once. It follows every control that shares the topic." },
  { code: "03", name: "Risk Register",        copy: "Inherent → residual, tied to mitigating controls, live." },
  { code: "04", name: "Policies",             copy: "Author, publish, acknowledge — with reminders that don't nag." },
  { code: "05", name: "Vendors & TPRM",       copy: "Third-party inventory, DPAs, security reviews, refresh cadence." },
  { code: "06", name: "DPIA",                 copy: "Assess personal-data risks against DPDP, GDPR and beyond." },
  { code: "07", name: "Training",             copy: "Assign, track, certify. Auditor-ready proof of completion." },
  { code: "08", name: "Reports",              copy: "Sealed PDF / CSV bundles. Framework-scoped, timestamped, dated." },
  { code: "09", name: "Activity Log",         copy: "Hash-chained. Tamper-evident. Independently verifiable." },
];

// The clause-spine (repeats down the left margin of the page)
export const CLAUSE_SPINE = [
  "A.5.1","A.5.23","A.8.2","A.8.5","CC6.1","CC7.2","Art.32","Art.25",
  "§8","§9","§164.312(a)","§164.312(b)","8.2.1","8.3.1","PR.AC-4","PR.AC-1",
  "AC-2","AC-3","6.2.1","A.7.4.6","CLD.6.3","A.11.4","6.1","6.2","§1798.100","§404",
];

// The full canonical layer — 68 real topics that sit beneath every framework.
// Grouped by family so the collapse grid reads like a real topic library.
export const CANONICAL_TOPICS_68 = [
  // Access & Identity (11)
  "Access Review", "Least Privilege", "Authorization", "Authentication", "Multi-Factor Auth",
  "Access Provisioning", "Access Revocation", "Privileged Access", "Session Management",
  "Password Policy", "Identity Federation",
  // Data Protection (10)
  "Encryption at Rest", "Encryption in Transit", "Key Management", "Data Classification",
  "Data Retention", "Data Deletion", "Backup & Restore", "Data Masking",
  "Data Loss Prevention", "PII / Personal Data",
  // Logging & Monitoring (6)
  "Audit Logging", "Log Integrity", "Monitoring & Alerting", "SIEM Integration",
  "Anomaly Detection", "Log Retention",
  // Network & Infra (7)
  "Network Segmentation", "Firewall Rules", "DDoS Protection", "Endpoint Protection",
  "Vulnerability Management", "Patch Management", "Secure Baselines",
  // App & Change (6)
  "Secure SDLC", "Code Review", "Change Management", "Configuration Management",
  "Release Management", "Environment Separation",
  // Vendors & Supply (5)
  "Vendor Inventory", "Vendor DPA", "Vendor Risk Review", "Sub-processor Register", "SLA & KPI Monitoring",
  // Governance & Risk (7)
  "Risk Assessment", "Risk Register", "Risk Treatment Plan", "Policies & Standards",
  "Roles & Responsibilities", "Board Oversight", "Compliance Reporting",
  // Privacy (7)
  "Privacy Notice", "Consent Management", "Data Subject Rights", "DPIA",
  "Cross-Border Transfer", "Records of Processing", "Retention Schedule",
  // Incident & Continuity (5)
  "Incident Response", "Breach Notification", "Forensics Readiness",
  "Business Continuity", "Disaster Recovery",
  // People (4)
  "Security Awareness", "Onboarding Training", "Role-based Training", "Acceptable Use",
];

// Marquee (editorial slow scroll)
export const MARQUEE_CODES = [
  "ISO 27001 · A.5.23","SOC 2 · CC6.1","GDPR · Art.32","DPDP · §8",
  "HIPAA · §164.312(a)","PCI DSS · 8.2.1","NIST CSF · PR.AC-4","NIST 800-53 · AC-2",
  "ISO 42001 · 6.2.1","ISO 27701 · A.7.4.6","ISO 27017 · CLD.6.3","ISO 27018 · A.11.4",
  "CIS v8 · 6.1","CCPA · §1798.100","SOX · §404","SOC 1 · CC5.2",
  "FedRAMP · AC-2","TISAX · 1.2.1","Cyber Essentials · CE.1","Essential Eight · E8.1",
];
